<template>
	<div ref="root" class="multi-factor__input" :class="[{ 'multi-factor__input--loading' : showLoading }, inputClass]">
		<label class="cog-label mb-2">{{ label }}</label>
		<PincodeInput
			v-model="code"
			placeholder="•"
			:length="6"
			:autofocus="autofocus ? !disabled : false"
			value="code"
			class="multi-factor__input-fields flex flex--center w-100 mb-2"
			:class="inputClasses"
		/>
		<div class="multi-factor__input-message flex flex--align flex--center sz-2">
			<Inline v-if="iconName" :svg="iconName" :class="[{ 'spin' : showLoading }, iconColorClass]" />
			<p class="m-0 ml-1"> {{ message }}</p>
		</div>
	</div>
</template>

<script lang="ts">
	import { computed, nextTick, onMounted, ref, watch, defineComponent } from 'vue';
	import PincodeInput from 'vue-pincode-input';

	export default defineComponent({
		name: 'MultifactorInput',
		components: {
			PincodeInput
		},
		props: {
			isValidated: { type: Boolean, default: false },
			isLoading: { type: Boolean, default: false },
			inputClasses: { type: String, default: null },
			label: { type: String, default: 'Enter code from authenticator app' },
			disabled: { type: Boolean, default: false },
			autofocus: { type: Boolean, default: false }
		},
		setup(props, { emit }) {
			const code = ref('');
			const isValidatedInternal = ref(props.isValidated);
			const error = ref(false);
			const root = ref<Element | null>(null);

			const showLoading = computed(() => props.isLoading && !isValidatedInternal.value);

			const message = computed(() => {
				if (showLoading.value)
					return 'Validating';
				if (code.value.length === 6)
					return isValidatedInternal.value ? 'Code authenticated.' : 'Invalid code.';
				else if (code.value.length > 0)
					return '';
				else if (error.value)
					return 'Invalid code.';
				return '';
			});

			const iconName = computed(() => {
				if (showLoading.value)
					return 'cog';
				else if (code.value.length === 6)
					return isValidatedInternal.value ? 'success' : 'error';
				else if (error.value)
					return 'error';
				return '';
			});

			const iconColorClass = computed(() => {
				if (iconName.value === 'error') {
					return 'text-icon-error-high-contrast';
				}
				else if (iconName.value === 'warning') {
					return 'text-icon-warning-high-contrast';
				}
				else if (iconName.value === 'success') {
					return 'text-icon-success-high-contrast';
				}
				return '';
			});

			const inputClass = computed(() => {
				if (code.value.length === 6 && !showLoading.value)
					return isValidatedInternal.value ? 'multi-factor__input--success' : 'multi-factor__input--error';
				else if (error.value)
					return 'multi-factor__input--error';
				return '';
			});

			const disableInputs = () => {
				const disabled = props.disabled || isValidatedInternal.value;
				const inputs = root.value.getElementsByTagName('input');

				for (const i of inputs) {
					i.disabled = disabled;

					if (disabled)
						i.tabindex = -1;
					else
						i.tabindex = 0;
				}

				if (!disabled && props.autofocus)
					inputs[0].focus();
			};

			watch([() => props.disabled, isValidatedInternal], disableInputs);

			watch(code, () => {
				if (code.value.length > 0)
					error.value = false;
				if (code.value.length === 6)
					emit('complete', code.value);
				else
					isValidatedInternal.value = false;
			});

			watch([() => props.isLoading, () => props.isValidated], () => {
				if (!props.isLoading) {
					isValidatedInternal.value = props.isValidated;

					if (!isValidatedInternal.value) {
						error.value = true;
						code.value = '';
					}

					// disabling code input once it's been validated
					if (isValidatedInternal.value) {
						disableInputs();
					}
				}
			});

			onMounted(async () => {
				await nextTick();
				if (props.disabled)
					disableInputs();
			});

			return {
				code,
				isValidatedInternal,
				error,
				message,
				root,
				iconName,
				iconColorClass,
				inputClass,
				showLoading
			};
		}
	});
</script>

<style lang="scss">
.multi-factor__input {

	label {
		display: block;
		font-size: .9rem;
		text-align: center;
	}

	&--loading {

		.multi-factor__input-message {
			opacity: 0;
			animation: loading-message .25s forwards;
			animation-delay: 1s;

			@keyframes loading-message {

				from {
					opacity: 0;
				}

				to {
					opacity: 1;
				}
			}
		}
	}

	&--success {

		.multi-factor__input-message {
			color: var(--green-6);

			svg {
				color: var(--border-success);
			}
		}

		.multi-factor__input-fields input {
			border-bottom: 2px solid transparent;
		}
	}

	&--error {

		.multi-factor__input-message {
			color: var(--text-error);
		}

		.multi-factor__input-fields {
			animation: shake 1s;

			input {
				border-bottom: 2px solid var(--border-error);
				color: var(--text-error);

				&::placeholder {
					color: var(--text-error);
				}
			}
		}
	}

	&-fields {

		input {
			flex-grow: 1;
			width: 100%;
			min-width: 0;
			max-width: unset !important;
			background: none;
			border: 0;
			border-bottom: 2px solid var(--shade-9);
			border-radius: 0;
			box-shadow: none;
			color: var(--shade-9);
			font-size: 1.75rem;
			font-weight: 400;
			transition: opacity .25s;

			&::placeholder {
				color: var(--shade-9);
				transition: opacity .25s;
			}

			&:placeholder-shown:not(:focus),
			&:focus::placeholder {
				opacity: .5;
				transition: opacity .25s;
			}

			&:not(:last-child) {
				margin-right: 10px;
			}

			&:focus {
				box-shadow: none;
			}
		}
	}

	&-message {
		height: 1.5rem;
	}
}
</style>